import React, { useState, useEffect, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from './Navbar';
import { useNavigate } from 'react-router-dom';
import { SocialStatusContainer } from './SocialComponents';
import { useSocialStatus } from './SocialStatusUpdater';

const GlitchText = ({ text, className }) => (
  <span className={`relative inline-block ${className} text-purple-500 animate-pulse`}>
    {text}
  </span>
);

const renderWebsite = (website) => {
  try {
    const parsedUrl = new URL(website);
    return (
      <a 
        href={website} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="text-purple-500 hover:underline"
      >
        🌐 {parsedUrl.hostname}
      </a>
    );
  } catch (e) {
    return <span className="text-slate-300">{website}</span>;
  }
};

const PublicUserProfile = memo(({ username }) => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState('Profile');
  const [bannerSrc, setBannerSrc] = useState(null);
  const [bannerFailed, setBannerFailed] = useState({ gif: false, png: false });
  const navigate = useNavigate();

  const getDiscordAvatarUrl = (social) => {
    return social?.platform === 'discord' && social.avatarHash
      ? `https://cdn.discordapp.com/avatars/${social.socialId}/${social.avatarHash}.png?size=128`
      : null;
  };

  const getSteamAvatarUrl = (social) => {
    return social?.platform === 'steam' && social.avatarHash
      ? `https://steamcdn-a.akamaihd.net/steamcommunity/public/images/avatars/${social.avatarHash.substring(0, 2)}/${social.avatarHash}.jpg`
      : null;
  };

  const getDiscordBannerUrl = (social) => {
    if (social?.platform !== 'discord' || !social.bannerHash) return null;
    const baseUrl = `https://cdn.discordapp.com/banners/${social.socialId}/${social.bannerHash}`;
    return `${baseUrl}.gif?size=600`;
  };

  const getWebSocketDiscordAvatar = (platform, socialId, statusData) => {
    if (platform === 'discord') {
      return statusData?.[platform]?.[socialId]?.avatarUrl || null;
    }
    return null;
  };

  const getWebSocketDiscordBanner = (platform, socialId, statusData) => {
    if (platform === 'discord') {
      return statusData?.[platform]?.[socialId]?.bannerUrl || null;
    }
    return null;
  };

  const getSocialAvatar = (platform, socialId, social, statusData) => {
    if (platform === 'discord' && statusData?.[platform]?.[socialId]?.bannerUrl) {
      return getWebSocketDiscordAvatar('discord', socialId, statusData);
    } else if (platform === 'discord') {
      return getDiscordAvatarUrl(social);
    } else if (platform === 'steam') {
      return getSteamAvatarUrl(social);
    }
    return null;
  };

  useEffect(() => {
    const fetchPublicProfile = async () => {
      if (!username) {
        setError('No username provided');
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(`https://api.4fingerstudios.com/api/auth/profile/${username}`);
        if (response.status === 404) {
          setError('User not found');
          setLoading(false);
          return;
        }
        if (!response.ok) throw new Error('Failed to fetch profile');
        const data = await response.json();
        setProfileData(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    
    fetchPublicProfile();
  }, [username]);

  const subscriptions = React.useMemo(() => {
    const subs = {};
    if (profileData?.socials) {
      const steamSocial = profileData.socials.find(s => s.platform === 'steam');
      const discordSocial = profileData.socials.find(s => s.platform === 'discord');
      if (steamSocial?.socialId) {
        subs.steam = [steamSocial.socialId];
      }
      if (discordSocial?.socialId) {
        subs.discord = [discordSocial.socialId];
      }
    }
    return subs;
  }, [profileData]);

  const { statusData, isConnecting, connectionError } = useSocialStatus(subscriptions, localStorage.getItem('token'));

  // Log subscriptions and statusData for debugging
  useEffect(() => {
    console.log('Subscriptions:', subscriptions);
    console.log('statusData:', statusData);
  }, [subscriptions, statusData]);

  const discordSocial = profileData?.socials?.find(s => s.platform === 'discord');
  const steamSocial = profileData?.socials?.find(s => s.platform === 'steam');
  const discordId = subscriptions.discord?.[0];
  const discordStatus = discordId ? statusData.discord?.[discordId] : null;

  useEffect(() => {
    if (discordSocial) {
      // Prefer WebSocket data if available
      const wsBannerUrl = statusData?.discord?.[discordSocial.socialId]
        ? getWebSocketDiscordBanner('discord', discordSocial.socialId, statusData)
        : null;
      
      if (wsBannerUrl) {
        setBannerSrc(wsBannerUrl);
        setBannerFailed({ gif: false, png: false });
      } else {
        // Fallback to profile data if WebSocket data is unavailable
        const profileBannerUrl = getDiscordBannerUrl(discordSocial);
        setBannerSrc(profileBannerUrl);
        setBannerFailed({ gif: false, png: false });
      }
    } else {
      setBannerSrc(null);
    }
  }, [statusData, profileData?.socials]);

  const handleBannerError = (type) => {
    if (type === 'gif' && !bannerFailed.gif) {
      setBannerFailed((prev) => ({ ...prev, gif: true }));
      if (discordSocial) {
        const wsBannerUrl = statusData?.discord?.[discordSocial.socialId]
          ? getWebSocketDiscordBanner('discord', discordSocial.socialId, statusData)
          : getDiscordBannerUrl(discordSocial);
        if (wsBannerUrl) {
          setBannerSrc(wsBannerUrl.replace('.gif', '.png'));
        } else {
          setBannerSrc(null);
        }
      }
    } else if (type === 'png' && !bannerFailed.png) {
      setBannerFailed((prev) => ({ ...prev, png: true }));
      setBannerSrc(null);
    }
  };

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.4 } },
  };

  const cardVariants = {
    initial: { scale: 0.95, opacity: 0 },
    animate: { scale: 1, opacity: 1, transition: { duration: 0.4, ease: 'easeOut' } },
  };

  const tabContentVariants = {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, x: 20, transition: { duration: 0.3 } },
  };

  const tabs = ['Profile', 'Stats', 'Socials', 'Gaming'];

  const profileImage = discordStatus?.avatar
    ? discordStatus.avatar
    : discordSocial && statusData?.discord?.[discordSocial.socialId]
    ? getWebSocketDiscordAvatar('discord', discordSocial.socialId, statusData)
    : steamSocial
    ? getSteamAvatarUrl(steamSocial)
    : profileData?.profileImageUrl || 'https://cdn.4fingerstudios.com/assets/images/istockphoto-518552551-612x612.jpg';

  return (
    <motion.div
      className="flex flex-col min-h-screen text-purple-300 font-mono overscroll-none relative"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <div className="fixed inset-0 z-[-1]">
        {bannerSrc && !bannerFailed.png ? (
          <picture>
            <source srcSet={bannerSrc} type={bannerSrc.endsWith('.gif') ? 'image/gif' : 'image/png'} />
            <img
              src={bannerSrc}
              alt="Profile banner"
              className="w-full h-full object-cover filter blur-md opacity-30"
              onError={() => {
                const isGif = bannerSrc.split('?')[0].endsWith('.gif');
                handleBannerError(isGif ? 'gif' : 'png');
              }}
            />
          </picture>
        ) : (
          <div className="w-full h-full bg-gradient-to-b from-gray-900 to-slate-900 opacity-50">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(124,58,237,0.08)_0%,transparent_100%)] animate-pulse" />
          </div>
        )}
      </div>

      <Navbar currentPage="Profile" />

      {error && (
        <div className="fixed top-4 left-4 right-4 sm:left-1/2 sm:right-auto sm:transform sm:-translate-x-1/2 z-50 bg-red-900/80 text-red-200 px-4 py-2 rounded-md shadow-lg flex items-center max-w-md mx-auto">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="flex-1">{error}</span>
        </div>
      )}

      {isConnecting && (
        <div className="fixed top-4 left-4 right-4 sm:left-1/2 sm:right-auto sm:transform sm:-translate-x-1/2 z-50 bg-slate-900/80 text-slate-400 px-4 py-2 rounded-md shadow-lg flex items-center max-w-md mx-auto">
          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-purple-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="flex-1">Connecting to status service...</span>
        </div>
      )}

      {connectionError && (
        <div className="fixed top-4 left-4 right-4 sm:left-1/2 sm:right-auto sm:transform sm:-translate-x-1/2 z-50 bg-red-900/80 text-red-200 px-4 py-2 rounded-md shadow-lg flex items-center max-w-md mx-auto">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="flex-1">{connectionError}</span>
        </div>
      )}

      <main className="relative flex-grow pt-20 sm:pt-32 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        {loading ? (
          <div className="text-center text-slate-400 text-lg">Loading profile...</div>
        ) : profileData ? (
          <>
            <motion.section variants={cardVariants} className="bg-black/80 border border-purple-500/30 p-6 sm:p-8 rounded-xl">
              <div className="flex flex-col items-center">
                <div className="relative">
                  <motion.img
                    src={profileImage}
                    alt="Profile"
                    className="w-28 h-28 sm:w-32 sm:h-32 rounded-full object-cover border-2 border-purple-500/30"
                    whileHover={{ scale: 1.05 }}
                  />
                </div>
                
                <div className="text-center mt-4 space-y-1">
                  <h1 className="text-2xl sm:text-3xl font-bold text-white">{profileData.displayName || profileData.username}</h1>
                  <p className="text-purple-300/80 text-sm">@{profileData.username}</p>
                  
                  <p className="text-slate-300 text-sm sm:text-base mt-3">{profileData.bio || 'No bio yet.'}</p>
                  
                  <div className="flex flex-wrap justify-center gap-4 mt-4">
                    {profileData.location && (
                      <span className="text-slate-400 text-sm">📍 {profileData.location}</span>
                    )}
                    {profileData.website && renderWebsite(profileData.website)}
                  </div>
                </div>
              </div>
            </motion.section>

            <div className="sticky top-16 z-10 bg-slate-900/80 backdrop-blur-sm py-4 -mx-4 sm:mx-0 sm:bg-transparent sm:backdrop-blur-none">
              <div className="flex flex-wrap justify-center gap-2 sm:gap-4 border-b border-purple-500/20">
                {tabs.map((tab) => (
                  <motion.button
                    key={tab}
                    onClick={() => setCurrentTab(tab)}
                    className={`px-4 py-2 text-xs sm:text-sm uppercase tracking-wider rounded-md ${
                      currentTab === tab ? 'bg-purple-600 text-white' : 'text-slate-300 hover:bg-purple-500/10'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {tab}
                  </motion.button>
                ))}
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div key={currentTab} variants={tabContentVariants} initial="initial" animate="animate" exit="exit">
                {currentTab === 'Profile' && (
                  <motion.div className="bg-black/60 border border-purple-500/20 p-6 rounded-xl" variants={cardVariants}>
                    <h3 className="text-lg font-semibold mb-4">Profile Details</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm sm:text-base">
                      <p className="text-slate-300">Display Name: {profileData.displayName || profileData.username}</p>
                      <p className="text-slate-300">Username: @{profileData.username}</p>
                      <p className="text-slate-300">Bio: {profileData.bio || 'Not set'}</p>
                      <p className="text-slate-300">Location: {profileData.location || 'Not set'}</p>
                      <p className="text-slate-300">
                        Website:{' '}
                        {profileData.website ? renderWebsite(profileData.website) : 'Not set'}
                      </p>
                    </div>
                  </motion.div>
                )}
                {currentTab === 'Stats' && (
                  <motion.div className="bg-black/60 border border-purple-500/20 p-6 rounded-xl" variants={cardVariants}>
                    <h3 className="text-lg font-semibold mb-4">Profile Stats</h3>
                    <ul className="space-y-4 text-sm sm:text-base">
                      <li className="text-slate-300 flex items-center space-x-2">
                        <span className="text-purple-500">•</span>
                        <span>Joined on {new Date(profileData.createdAt).toLocaleDateString()}</span>
                      </li>
                      <li className="text-slate-300 flex items-center space-x-2">
                        <span className="text-purple-500">•</span>
                        <span>Last updated on {new Date(profileData.updatedAt).toLocaleDateString()}</span>
                      </li>
                    </ul>
                  </motion.div>
                )}
                {currentTab === 'Socials' && (
                  <motion.div className="bg-black/60 border border-purple-500/20 p-6 rounded-xl" variants={cardVariants}>
                    <h3 className="text-lg font-semibold mb-4">Connected Socials</h3>
                    {profileData.socials && profileData.socials.length > 0 ? (
                      <ul className="space-y-4 text-sm sm:text-base">
                        {profileData.socials.map((social, index) => (
                          <li key={index} className="flex items-center space-x-2">
                            <span className="text-purple-500">•</span>
                            <span className="flex items-center gap-2">
                              {social.platform === 'discord' && (
                                <img
                                  src={getSocialAvatar(social.platform, social.socialId, social, statusData)}
                                  alt={`${social.platform} avatar`}
                                  className="w-6 h-6 rounded-full"
                                />
                              )}
                              {social.platform === 'steam' && (
                                <img
                                  src={getSocialAvatar(social.platform, social.socialId, social, statusData)}
                                  alt={`${social.platform} avatar`}
                                  className="w-6 h-6 rounded-full"
                                />
                              )}
                              <span className="capitalize">{social.platform}</span>
                              <span className="text-slate-300">: {social.displayName}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-slate-400">No connected socials.</p>
                    )}
                  </motion.div>
                )}
                {currentTab === 'Gaming' && (
                  <motion.div variants={cardVariants}>
                    <SocialStatusContainer 
                      statusData={statusData}
                      isConnecting={isConnecting}
                      connectionError={connectionError}
                      profile={profileData}
                    />
                  </motion.div>
                )}
              </motion.div>
            </AnimatePresence>
          </>
        ) : (
          <div className="text-center text-slate-400 text-lg">Profile not found</div>
        )}
      </main>

      <footer className="relative bg-slate-900/80 text-slate-400 py-12 border-t border-slate-800/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            <div>
              <h4 className="text-purple-300 font-bold mb-4">4FingerStudios</h4>
            </div>
            <div>
              <h4 className="text-purple-300 font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="https://4fingerstudios.com" className="hover:text-purple-300 transition-colors duration-300">Home</a></li>
                <li><a href="https://4fingerstudios.com/contact" className="hover:text-purple-300 transition-colors duration-300">Contact</a></li>
                <li><a href="https://4fingerstudios.com/Privacy" className="hover:text-purple-300 transition-colors duration-300">Privacy Policy</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-purple-300 font-bold mb-4">Contact Us</h4>
              <p className="text-sm mb-2">
                <span className="text-purple-300 mr-2">Email:</span> 4.finger.studios.com@gmail.com
              </p>
              <p className="text-sm">
                <span className="text-purple-300 mr-2">Support:</span> support@4fingerstudios.com
              </p>
            </div>
          </div>
          <div className="mt-12 pt-6 border-t border-slate-800/50 text-center text-xs">
            <p>© {new Date().getFullYear()} 4FingerStudios. All rights reserved.</p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-indigo-500/20 to-transparent"></div>
      </footer>
    </motion.div>
  );
});

export default PublicUserProfile;